import { useState } from 'react'
import type { ServerApiClient } from '../../../data/ServerApiClient'
import { formatAsOf } from './cfoFormat'

// CfoStatementFlagModal — opens when a user clicks a per-line AI flag on a financial statement.
// Explains what the AI found, lets the user CONFIRM ("you're thinking about this right") or CORRECT it
// ("here's what actually happened"), and offers to dig into the underlying transactions. The answer is
// saved as durable memory (close.belief, keyed by node_id) and authors closed-loop forecast/metric
// directives — and the prior choice round-trips when the same flag is reopened.

export type FlagLine = {
  node_id: string
  label?: string | null
  severity?: string
  kind?: string
  finding?: string
  /** The AI's actual question about this line, stated verbatim (present whenever an open
   *  close.question backs this flag — answering it clears the flag). */
  question?: string | null
  question_claim_id?: string | null
  /** A balance-sheet anomaly drills into the month where the balance moved,
   * rather than the later as-of month that merely carries the balance. */
  drilldown_period?: string | null
  user_note?: { text?: string; mode?: string; claim_id?: string } | null
}

const sevTone = (sev: string | undefined): string =>
  sev === 'high' ? 'var(--neg)' : sev === 'medium' ? '#b8860b' : 'var(--muted, #888)'

export function CfoStatementFlagModal({
  appApi,
  workbookId,
  statement,
  period,
  line,
  onClose,
  onAnswered,
  onViewGl,
}: {
  appApi: ServerApiClient
  workbookId: string
  statement: string
  period: string
  line: FlagLine
  onClose: () => void
  onAnswered: () => void
  onViewGl?: (nodeId: string, periodOverride?: string | null) => void
}) {
  const question = (line.question || '').trim() || (line.kind === 'question' ? (line.finding || '').trim() : '')
  const hasQuestion = question.length > 0
  const priorMode = line.user_note?.mode === 'confirm' ? 'confirm' : line.user_note?.text ? 'correct' : null
  // An open question wants an answer — open the free-text box immediately instead of hiding it
  // behind the "Actually…" toggle.
  const [mode, setMode] = useState<'confirm' | 'correct' | null>(priorMode ?? (hasQuestion ? 'correct' : null))
  const [text, setText] = useState(line.user_note?.mode === 'correct' ? line.user_note?.text || '' : '')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ note?: string; directives?: any[] } | null>(null)
  const [error, setError] = useState('')

  const submit = (m: 'confirm' | 'correct') => {
    setBusy(true)
    setError('')
    appApi
      .answerQuickBooksFinancialsFlag(workbookId, {
        node_id: line.node_id,
        statement,
        period,
        label: line.label || undefined,
        finding: line.finding || undefined,
        mode: m,
        text: m === 'correct' ? text.trim() : '',
        claim_id: line.question_claim_id || undefined,
      })
      .then((r) => {
        setResult({ note: r.note, directives: r.directives })
        onAnswered()
      })
      .catch((e: any) => setError(String(e?.message || e)))
      .finally(() => setBusy(false))
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.32)',
        zIndex: 90010,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rise"
        style={{
          width: 'min(560px, 100%)',
          maxHeight: '86vh',
          overflowY: 'auto',
          background: 'var(--surface-1, #fff)',
          borderRadius: 16,
          padding: '20px 22px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span aria-hidden style={{ color: sevTone(line.severity), fontSize: 14 }}>✦</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: sevTone(line.severity), textTransform: 'capitalize' }}>
            {(line.kind || 'flag').replace(/_/g, ' ')}
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} aria-label="Close" style={{ fontSize: 13 }}>
            ✕
          </button>
        </div>

        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>{line.label || 'This line'}</div>
        {line.kind === 'question' && hasQuestion ? null : (
          <p style={{ margin: '0 0 14px', fontSize: 15, lineHeight: 1.55 }}>
            {line.finding || 'The AI flagged this line for review.'}
          </p>
        )}
        {hasQuestion ? (
          <div
            style={{
              margin: '0 0 14px',
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(184,134,11,0.08)',
              border: '1px solid rgba(184,134,11,0.30)',
              fontSize: 15,
              lineHeight: 1.55,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: '#b8860b', display: 'block', marginBottom: 4 }}>
              The AI asked
            </span>
            {question}
          </div>
        ) : null}

        {onViewGl ? (
          <button
            className="btn ghost"
            onClick={() => onViewGl(line.node_id, line.drilldown_period)}
            style={{ marginBottom: 16, fontSize: 13 }}
          >
            {line.drilldown_period ? `View the ${formatAsOf(line.drilldown_period)} transactions →` : 'View the transactions →'}
          </button>
        ) : null}

        {result ? (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 12,
              background: 'rgba(14,124,134,0.10)',
              border: '1px solid rgba(14,124,134,0.30)',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pos)', marginBottom: 4 }}>Saved ✓</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{result.note}</div>
            {result.directives && result.directives.length ? (
              <div style={{ fontSize: 13, marginTop: 8, color: 'var(--muted, #555)' }}>
                This updates your{' '}
                {result.directives
                  .map((d: any) => (String(d.directive_kind).startsWith('forecast.') ? 'forecast' : 'ratios'))
                  .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
                  .join(' + ')}
                {result.directives.some((d: any) => String(d.directive_kind).startsWith('forecast.'))
                  ? ' (pending the believability check).'
                  : '.'}
              </div>
            ) : null}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className="btn" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div style={{ borderTop: '1px solid var(--border, #eee)', paddingTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              {hasQuestion ? 'Answer the AI — this clears the question.' : 'Is the AI thinking about this correctly?'}
            </div>
            {line.user_note ? (
              <div style={{ fontSize: 12.5, color: 'var(--muted, #888)', marginBottom: 10 }}>
                You previously {line.user_note.mode === 'confirm' ? 'confirmed this.' : `noted: “${line.user_note.text}”`}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <button
                className="btn"
                aria-pressed={mode === 'confirm'}
                onClick={() => submit('confirm')}
                disabled={busy}
                style={
                  mode === 'confirm'
                    ? { background: 'rgba(14,124,134,0.14)', color: 'var(--pos)', borderColor: 'var(--pos)' }
                    : undefined
                }
              >
                {hasQuestion ? '✓ Nothing to flag here' : "✓ That's right"}
              </button>
              {hasQuestion ? null : (
                <button
                  className="btn ghost"
                  aria-pressed={mode === 'correct'}
                  onClick={() => setMode('correct')}
                  disabled={busy}
                  style={mode === 'correct' ? { borderColor: '#b8860b', color: '#b8860b' } : undefined}
                >
                  Actually…
                </button>
              )}
            </div>
            {mode === 'correct' ? (
              <div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  autoFocus={hasQuestion}
                  placeholder={
                    hasQuestion
                      ? 'Answer in your words — e.g. “that account moved to a new matter code; nothing is missing.”'
                      : 'Tell the AI what actually happened — e.g. “this was a one-time vendor credit, not a real cost drop.”'
                  }
                  rows={3}
                  style={{ width: '100%', borderRadius: 10, padding: 10, resize: 'vertical', fontSize: 14 }}
                />
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <button className="btn" disabled={busy || !text.trim()} onClick={() => submit('correct')}>
                    {busy ? 'Saving…' : hasQuestion ? 'Send answer' : 'Save'}
                  </button>
                </div>
              </div>
            ) : null}
            {error ? (
              <div style={{ fontSize: 13, color: 'var(--neg)', marginTop: 8 }}>{error}</div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default CfoStatementFlagModal
